import { useState, useEffect } from 'react';
import { WebSocketMessage } from '../types';
import { HydroponicWebSocket, WebSocketEventHandler } from '../services/websocket';

// Globální instance WebSocket pro sdílení napříč komponenty
let globalWebSocket: HydroponicWebSocket | null = null;

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Pokud již není WebSocket instance, vytvořit novou
    if (!globalWebSocket) {
      globalWebSocket = new HydroponicWebSocket(''); // Bez defaultního device ID
    }

    const handleConnected = () => {
      setIsConnected(true);
      setIsReconnecting(false);
      setError(null);
    };

    const handleError = (message: WebSocketMessage) => {
      if (message.type === 'error') {
        // Handle both flattened message and data.error patterns
        const errorMessage =
          (message as any).message ||
          (message.data as any)?.error ||
          (typeof message.data === 'string' ? message.data : 'WebSocket connection error');

        setError(errorMessage);
        setIsConnected(false);
      }
    };

    const handleWelcome = () => {
      setIsConnected(true);
      setIsReconnecting(false);
      setError(null);
    };

    // Přidat event listenery
    globalWebSocket.on('welcome', handleWelcome);
    globalWebSocket.on('pong', handleConnected);
    globalWebSocket.on('error', handleError);

    // Připojit WebSocket pokud není připojen
    if (!globalWebSocket.isConnected()) {
      globalWebSocket.connect().catch((error) => {
        console.error('WebSocket připojení selhalo:', error);
        setError('Nepodařilo se připojit k WebSocket serveru');
        setIsConnected(false);
      });
    } else {
      setIsConnected(true);
    }

    return () => {
      if (globalWebSocket) {
        globalWebSocket.off('welcome', handleWelcome);
        globalWebSocket.off('pong', handleConnected);
        globalWebSocket.off('error', handleError);
      }
    };
  }, []);

  const connect = () => {
    if (globalWebSocket) {
      globalWebSocket.connect().catch((error) => {
        console.error('WebSocket připojení selhalo:', error);
        setError('Nepodařilo se připojit k WebSocket serveru');
        setIsConnected(false);
      });
    }
  };

  const disconnect = () => {
    if (globalWebSocket) {
      globalWebSocket.disconnect();
      setIsConnected(false);
    }
  };

  const subscribe = (type: string, id?: string) => {
    if (!globalWebSocket) return;
    // subscribeTopic = trvalý odběr (zapamatuje se a obnoví po reconnectu)
    if (type === 'device_status') {
      globalWebSocket.subscribeTopic(id ? `device-${id}/status` : 'device-status');
    } else if (type === 'sensor_data') {
      globalWebSocket.subscribeTopic(id ? `device-${id}/sensor_data` : 'sensor-data');
    } else if (type === 'alerts') {
      globalWebSocket.subscribeTopic('hydroponics/alerts/#');
    } else if (type === 'actuators') {
      globalWebSocket.subscribeTopic(id ? `device-${id}/actuators` : 'actuators');
    }
  };

  const unsubscribe = (type: string, id?: string) => {
    if (globalWebSocket) {
      if (type === 'device_status') {
        const topic = id ? `device-${id}/status` : 'device-status';
        globalWebSocket.send({
          type: 'unsubscribe',
          topic,
          timestamp: new Date().toISOString(),
        });
      } else if (type === 'sensor_data') {
        const topic = id ? `device-${id}/sensor_data` : 'sensor-data';
        globalWebSocket.send({
          type: 'unsubscribe',
          topic,
          timestamp: new Date().toISOString(),
        });
      } else if (type === 'alerts') {
        globalWebSocket.send({
          type: 'unsubscribe',
          topic: 'hydroponics/alerts/#',
          timestamp: new Date().toISOString(),
        });
      } else if (type === 'actuators') {
        const topic = id ? `device-${id}/actuators` : 'actuators';
        globalWebSocket.send({
          type: 'unsubscribe',
          topic,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  const on = (type: string, handler: WebSocketEventHandler) => {
    if (globalWebSocket) {
      globalWebSocket.on(type, handler);
    }
  };

  const off = (type: string, handler: WebSocketEventHandler) => {
    if (globalWebSocket) {
      globalWebSocket.off(type, handler);
    }
  };

  return {
    isConnected,
    isReconnecting,
    error,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    on,
    off,
  };
};

export const useWebSocketData = <T>(eventType: string, initialData: T) => {
  const [data, setData] = useState<T>(initialData);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    const handleMessage = (message: WebSocketMessage) => {
      if (message.type === eventType) {
        setData(message.data as T);
        setLastUpdate(new Date());
      }
    };

    if (globalWebSocket) {
      globalWebSocket.on(eventType, handleMessage);
    }

    return () => {
      if (globalWebSocket) {
        globalWebSocket.off(eventType, handleMessage);
      }
    };
  }, [eventType]);

  return { data, lastUpdate };
};
